import 'react-big-calendar/lib/css/react-big-calendar.css';

import { FormflowComponentSections } from '@components/FormFlow/FormflowComponent';
import Loading from '@components/Loading';
import PageContent from '@components/PageContent';
import PageHeader from '@components/PageHeader';
import PageSideNav from '@components/PageSideNav';
import PageWrapper from '@components/PageWrapper';
import SchedulerContainer from '@components/Scheduler/SchedulerContainer';
import AgentsPage from '@containers/Agents';
import AgentsAdminPage from '@containers/AgentsAdmin';
import LinksPage from '@containers/AlertLink';
import AlertsPage from '@containers/Alerts';
import AssignmentPage from '@containers/AssignmentContainer';
import AuditedDetailsPage from '@containers/AuditedDetails';
import AuditedsPage from '@containers/AuditedsContainer';
import AuditorDetailsPage from '@containers/AuditorDetails';
import AuditorsContainer from '@containers/AuditorsContainer';
import AuditSettings from '@containers/AuditSettings';
import ClientContainer from '@containers/ClientContainer';
import CodePage from '@containers/Code';
import DashboardPage from '@containers/Dashboard';
import DistributorsPage from '@containers/Distributors';
import HistoryPage from '@containers/History';
import HistoryAdminPage from '@containers/HistoryAdmin';
import HomePage from '@containers/Home';
import liteDashboard from '@containers/liteDashboard';
import PermissionsPage from '@containers/Permissions';
import { ProductsContainerComponent } from '@containers/Products';
import ProfilePage from '@containers/ProfileContainer';
import RecoverPage from '@containers/Recover';
import TicketDetailsPage from '@containers/TicketDetails';
import UnitsPage from '@containers/UnitContainer';
import { UsersContainer } from '@containers/UsersContainer';
import WorkflowContainer from '@containers/WorkflowContainer';
import { WorkstationProductionLineComponent } from '@containers/WorkstationProductionLineContainer';
import Permission from '@helpers/_permissions';
import NotificationComponent from '@helpers/_showNotifications';
import Unit from '@interfaces/IUnit';
import { IUser, IUserRole } from '@interfaces/IUser';
import { Pillar, PillarNames } from '@interfaces/pillar';
import AwsConfig from '@services/AwsConfig';
import { DefaultLocale } from '@services/Constants';
import AppContext from '@services/Context';
import RoleService from '@services/RoleService';
import UnitService from '@services/Unit';
import UserService from '@services/UserService';
import I18n from '@src/i18n';
import { ThemeClient } from '@src/styled-components';
import React, { Component, Fragment } from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';

import { injectGlobal } from '../../src/styled-components';
import { Routes } from '../helpers/_routes';
import ExecutionProcess from './ExecutionProcessComponent';
import { ExportContainer } from './ExportImportContainer';
import FormflowContainer from './FormflowContainer';
import { HistoryEHSSContainerComponent } from './HistoryEhssContainer';
import { HistoryQAContainerComponent } from './HistoryQAContainer';
import HomeNoPermission from './HomeNoPermissionContainer';
import LiteProcesses from './LiteOnboarding';
import { WrappedLoginContainer } from './LoginContainer';
import ManagerContainer from './ManagerContainer';
import NotificationsContainer from './NotificationsContainer';
import ProcessContainer, { ProcessContainerPage } from './ProcessContainer';
import ProcessManagement from './ProcessManagement';
import { ProductionLineComponent } from './ProductionLineContainer';
import { TargetsContainer } from './TargetsContainer';
import TicketDetailsEHSS from './TicketDetailsEhss';
import { UploadModel3DContainer } from './UploadModel3DContainer';
import WorkflowsContainer from './WorkflowsContainer';
import PermissionService from '@services/graphQL/graphQLPermission'
import uuidv4 from 'uuidv4';
import MyProcessesContainer from './MyProcessesContainer'
import SchedulingService from '@services/graphQLSchedulingService'
import { StatusProcess } from '@helpers/Enums'
import DashboardList from './DashboardList'

interface IAppProps {
  history: {
    replace: Function
  };
  match: any;
  location: any;
  pillar: any
};

// Unit and Units are nullable to pre-login state. From the moment the user logs in on, they (should) exist
interface IAppState {
  activeUnitId: string;
  authorized: boolean;
  client?: any;
  history?: any;
  isLoading: boolean;
  locale: string;
  pillar?: string;
  redirectToReferrer?: boolean;
  token?: {};
  units: Unit[];
  user?: IUser;
  roles?: string[];
  theme?: any;
  isLoggingIn: boolean;
  visibleOnboarding: boolean;
  shouldUpdateNotifications: boolean;
  userPermissions: any;
  defaultPermissions: any;
  updateNotificationVar: any;
  scheduled: any;
};

// These strings have to be just the same as the state, watch out.
export enum LocalStorageKeys {
  PILLAR = 'pillar',
  LOCALE = 'locale',
  UNITS = 'units',
  ACTIVE_ROLES = 'roles',
  ACTIVE_UNIT_ID = 'activeUnitId',
  CLIENT = 'client',
  USER_ID = 'userId',
  USER_NAME = 'username',
  CLIENT_LOCALES = 'clientLocales',
  CLIENT_UPLOAD = 'clientUpload',
  CLIENT_DEFAULT = 'clientDefault',
  TICKETID = 'ticketid',
  MODAL_ONBOARDING = 'modalonboarding',
  FIRSTACCESS = 'firstaccess',
};


export default class App extends Component<IAppProps, IAppState> {

  private readonly _unitSvc: UnitService;
  private readonly _userSvc: UserService;
  private readonly _roleSvc: RoleService;
  private readonly _permission: Permission;
  private readonly _i18n: I18n;
  private permissionService: any;
  private processGraphQL: any = new SchedulingService()
  private userId: any
  private interval: any


  private readonly NO_ACTIVE_UNIT = 'NO_ACTIVE_UNIT';
  // private readonly _graphQlClient:any;

  constructor(props: IAppProps) {
    super(props);

    this._permission = new Permission();
    this._unitSvc = new UnitService();
    this._userSvc = new UserService();
    this._roleSvc = new RoleService();
    this._i18n = new I18n();
    this.permissionService = new PermissionService()
    this.userId = localStorage.getItem('userId')
    // Methods binding. This way, vscode outline works! =)
    this.verifySession = this.verifySession.bind(this)
    this.getCurrentUnit = this.getCurrentUnit.bind(this);
    this.getCurrentLocale = this.getCurrentLocale.bind(this);

    this.state = {
      activeUnitId: this.getCurrentUnit(),
      authorized: false,
      isLoading: true,
      locale: this.getCurrentLocale(),
      redirectToReferrer: false,
      units: new Array<Unit>(),
      isLoggingIn: false,
      visibleOnboarding: false,
      shouldUpdateNotifications: false,
      userPermissions: [],
      defaultPermissions: [],
      updateNotificationVar: 'initial',
      scheduled: null
    }

    this.updateNotification = this.updateNotification.bind(this)
  }

  updateNotification() {
    this.setState({ updateNotificationVar: uuidv4() })
  }

  // There's always a unit AFTER the user logged in. That's why we return NO_ACTIVE_UNIT
  getCurrentUnit(): string {
    return LocalStorageKeys.ACTIVE_UNIT_ID in localStorage ? String(localStorage.getItem(LocalStorageKeys.ACTIVE_UNIT_ID)) : this.NO_ACTIVE_UNIT;
  }
  // There's always a default locale
  getCurrentLocale(): string {
    return String(LocalStorageKeys.LOCALE in localStorage ? localStorage.getItem(LocalStorageKeys.LOCALE) : DefaultLocale);
  };

  syncClientAndPillars = async () => {

    const client = LocalStorageKeys.CLIENT in localStorage ? JSON.parse(String(localStorage.getItem(LocalStorageKeys.CLIENT))) : await AwsConfig.init();
    const configIndex = client.pillars.findIndex((item: any) => item === 'CONFIG');
    await localStorage.setItem('processId', client.default && client.default.dashboard ? client.default.dashboard.processId : '')
    await localStorage.setItem('infoId', client.default && client.default.dashboard ? client.default.dashboard.infoId : '')
    await this.setState({ client });
    // Removes configIndex from the pillars array, since apparently it has no use
    client.pillars.splice(configIndex, 1);

    if (client.locales) {
      await localStorage.setItem(LocalStorageKeys.CLIENT_LOCALES, JSON.stringify(client.locales));
    }

    if (client.default) {
      await localStorage.setItem(LocalStorageKeys.CLIENT_DEFAULT, JSON.stringify(client.default))
    }

    if (client.storage) {
      await localStorage.setItem(LocalStorageKeys.CLIENT_UPLOAD, JSON.stringify(client.storage))
    }
    // If no pillar is set on state, tries to get from the local storage. 
    // In the event no pillar is located on the storage, sets the current pillar as the first one
    if (!this.state.pillar) {
      let pillar
      for (let item of client.pillars) {
        if (item !== PillarNames.CONFIG && item !== PillarNames.RS) {
          pillar = item;
        }
      }
      await this.setState({ pillar })
      pillar = LocalStorageKeys.PILLAR in localStorage && localStorage.pillar !== PillarNames.RS ? localStorage.getItem(LocalStorageKeys.PILLAR) : this.state.pillar;
      await this.syncStateAndLS(LocalStorageKeys.PILLAR, String(pillar));
    }
  }

  /**
   * This method syncs the units with the context API and the local storage. Ideally, localStorage wouldn't be needed, since there's a
   * context API, but life is hard sometimes. It also returns a boolean, since units are only bound to authorized users, so this
   * boolean is set to the state in order to drive the render. Also, there's a caveat to be taken into account: When there's no active unit
   * (before the login to be exact), I must store something in the state so the context api doesn't propagate bs everywhere. Having that
   * in mind, there's a placeholder unit called "NO_ACTIVE_UNIT", just to we can control when and where to update stuff. I know it's not
   * pretty, but what is?
   */
  syncUnits = async (): Promise<boolean> => {

    let authorized: boolean = false;

    try {
      authorized = await this._userSvc.isAuthenticated();

      // Units fetching and syncing are done here, because they can change quite often
      let _units: Unit[] = await this._unitSvc.listAll();
      const _myUnits: any = await this._userSvc.myUnits();
      _units = _units.filter(unit => _myUnits.units.includes(unit.id) === true);

      this.syncStateAndLS(LocalStorageKeys.UNITS, _units);

      /**
       * If there's an active Unit, we check if it's still valid. If, for some reason, the active unit doesn't exist on our
       * current units, we set the active one as the first that comes along
       */
      if (this.state.activeUnitId) {
        if (_units.find(x => x.id === this.state.activeUnitId) === undefined) {
          this.syncStateAndLS(LocalStorageKeys.ACTIVE_UNIT_ID, String(_units[0].id));
        }
      }
      // Nothing was found on the state, so we sync the first one anyways, because "there's always one unit" (JUCA, Clovis. 2018)
      else {
        this.syncStateAndLS(LocalStorageKeys.ACTIVE_UNIT_ID, String(_units[0].id));
      }
      await this.getMyRoles();

    } catch (error) {
      console.warn('unit fetching failed:', error);
      authorized = false;
    }

    return authorized;
  }


  // Overrides both state and local storage with a key/value pair
  syncStateAndLS = async (k: string, v: string | Object) => {

    const _obj = {};
    let _value: string = "";

    _obj[k] = v;

    _value = typeof v !== "string" ? JSON.stringify(v) : v;

    await this.setState(_obj, () => {
      // Just in case
      localStorage.removeItem(k);
      localStorage.setItem(k, _value);
    });
  };

  // Set parameters to state that don't change too frequently
  setStaticParams = async () => {

    let token: any = {};
    let user: IUser;

    try {
      // Gets token and convert to get values permissions
      token = await this._userSvc.getDecodedToken() || {};

      // Gets user
      user = await this._userSvc.me();
      await this.setState({
        token,
        user
      });

      localStorage.setItem(LocalStorageKeys.USER_ID, String(user.id));
    }

    catch (e) {
      throw new Error(`error on static parameters ${e}`)
    }
  }

  _login = async (username: string, password: string) => {
    let response: any;
    let authorized: boolean = false;
    await this.setState({ isLoggingIn: true });

    try {
      response = await this._userSvc.signIn(username, password);

      if (response) {

        await this.setStaticParams();

        authorized = await this.syncUnits(); // There's always a unit

        const { getUserGroupByUser } = await this.permissionService.getUserGroupByUser(response.storage.userId)
        const { listGroupPermissions } = await this.permissionService.listGroupPermissions();
        if (getUserGroupByUser) {
          await localStorage.setItem('userPermissions', JSON.stringify(getUserGroupByUser))
        }
        if (listGroupPermissions) {
          await localStorage.setItem('defaultPermissions', JSON.stringify(listGroupPermissions))
        }

        await this.setState({
          authorized,
          isLoading: false,
          redirectToReferrer: true,
          isLoggingIn: false,
          userPermissions: getUserGroupByUser,
          defaultPermissions: listGroupPermissions
        });

      } else {
        throw new Error('No response');
      }

    } catch (e) {
      await this.setState({
        isLoading: false,
        authorized,
        isLoggingIn: false,
      });

      NotificationComponent.Notify('error', I18n.l('titles.login'), e);

      this._logout();
    }
  };

  _logout = () => {
    this._userSvc.signOut();
    this.setState({
      authorized: false,
      isLoading: false,
      token: undefined
    });
    localStorage.setItem(LocalStorageKeys.USER_NAME, '');
    localStorage.setItem('userId', '');

    for (const _key in LocalStorageKeys) {
      localStorage.removeItem(_key);
    }
  };


  _changeUnit = async (unitId: string) => {
    await this.getMyRoles(unitId, undefined);
    await this.syncStateAndLS(LocalStorageKeys.ACTIVE_UNIT_ID, unitId);
  }

  _changeLocale = (locale: string) => {
    this.setState({ //to update react
      locale: this._i18n.change(locale)
    });
  }

  _changePillar = async (pillar: Pillar) => {
    await this.getMyRoles(undefined, pillar);
    await this.syncStateAndLS(LocalStorageKeys.PILLAR, pillar);


  }

  getMyRoles = async (unitChange?: any, pillarChange?: any) => {
    let roles: string[] = []
    if (this.state.user) {
      const user = await this._userSvc.me();
      const unit = unitChange || this.state.activeUnitId;
      const pillar = pillarChange || this.state.pillar;
      let allRoles: any = [];
      const userRoles: IUserRole[] = user.roles || [];
      const rolesActive: string[] = [];
      if (pillar === 'LI' || pillar === 'CM' || pillar === 'RS') {
        roles.push('ADMIN')
      } else {
        allRoles = await this._roleSvc.listAll();
      }

      userRoles.map(r => {
        if (r.unit === unit && r.pillar === pillar) {
          rolesActive.push(r.role)
        }
      })
      for (let r of allRoles) {
        if (rolesActive.indexOf(String(r.id)) !== -1) {
          roles.push(r.alias.toUpperCase())
        }
      }
    } else {
      roles.push("USER")
    }

    await localStorage.setItem('roles', JSON.stringify(roles));
    await this.setState({ roles })
  }
  verifySession() {
    let idSession = localStorage.getItem('userId')
    if (this.state.user && idSession !== this.state.user.id && window.location.href.search('login') === -1) {
      alert(I18n.l('common.sessionInvalid'))
      location.reload()
    }
  }
  componentDidMount() {
    // this.interval = setInterval(this.verifySession, 30000); //comentado para fazer testes(Gastl)
    this.initialData()

    ///////////////////Conpass//////////////////////////////
    if (process.env.NODE_ENV === 'development') {
      const script = document.createElement("script");
      script.src = "https://fast.conpass.io/K0sMhLe36uQIT.js";
      script.async = true;
      document.body.appendChild(script);
      let name = localStorage.getItem('username')
      script.onload = () => {
        Conpass.init({
          name: name,
          email: name
        })
      };
    }
    ///////////////////////////////////////////////////////
  }
  componentDidUpdate() {
    const element = document.getElementById("ze-snippet");
    if (this.state.pillar === 'EHSS') {
      const script = document.createElement("script");
      script.src = "https://static.zdassets.com/ekr/snippet.js?key=188057a4-eaad-4e35-b512-1e9cb5e92156";
      script.id = "ze-snippet"
      script.async = true;
      document.body.appendChild(script);
    }else{
      if(element) {
        element.remove();
        const iFrame = document.getElementById("launcher")
        if(iFrame) iFrame.remove();
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }
  async loadScheduled() {

    try {
      const { data } = await this.processGraphQL.getScheduleByUser(this.userId, [StatusProcess.PENDENT, StatusProcess.IN_PROGRESS])
      if (data && data.getSchedulesByUser && data.getSchedulesByUser.length) {
        let schedules = [...data.getSchedulesByUser]

        await this.setState({ scheduled: schedules })
      }
    } catch (e) {
      console.error('erro ao carregar processos agendados', e)
    }
  }

  initialData = async () => {
    await this.syncClientAndPillars();
    const themeClient = await ThemeClient();

    if (!this.state.token || this.state.user) {
      this.setStaticParams();
    }

    const authorized: boolean = await this.syncUnits();

    await this.getMyRoles();

    let userPermissions = localStorage.getItem('userPermissions')
    userPermissions = userPermissions ? JSON.parse(userPermissions) : []

    let defaultPermissions = localStorage.getItem('defaultPermissions')
    defaultPermissions = defaultPermissions ? JSON.parse(defaultPermissions) : []

    await this.setState({
      authorized,
      isLoading: false,
      theme: themeClient,
      userPermissions,
      defaultPermissions
    });

    await this.loadScheduled()
  }

  updateUnits = async () => {
    await this.syncUnits();
  }
  onChangeVisibleOnboarding = async () => {
    await this.setState({ visibleOnboarding: !this.state.visibleOnboarding })
  }

  updateNotifyContent = async (trueOrFalse: boolean) => {
    await this.setState({ shouldUpdateNotifications: trueOrFalse })
  }
  render() {
    const { isLoading, locale, activeUnitId, units, authorized, token, client, user, pillar, roles, theme } = this.state;
    const PrivateRoute = ({ component: Component, render, ...rest }: any) => {
      if (!Component) {
        Component = render;
      }
      const redirectRoute = authorized ? '/' : '/login'
      return (
        <Route {...rest} render={props => {
          return (
            this._permission.validate(authorized, props.location['pathname'], token, roles, user) ? (
              <Component {...props} />
            ) : (
                <Redirect
                  to={{
                    pathname: redirectRoute,
                    state: { from: props.location }
                  }}
                />
              )
          )
        }} />
      );
    }

    if (!!isLoading) {
      return <Loading />;
    }

    if (!client) {
      return (
        <h1>Not Found</h1>
      )
    }

    let userPermissions;
    if (this.state.userPermissions) {
      userPermissions = this.state.userPermissions;
    } else {
      userPermissions = localStorage.getItem('userPermissions');
    }
    return (
      <AppContext.Provider
        value={{
          activeUnitId,
          authorized,
          ChangeLocale: this._changeLocale,
          ChangePillar: this._changePillar,
          ChangeUnit: this._changeUnit,
          client,
          locale,
          Login: this._login,
          Logout: this._logout,
          pillar,
          theme: theme,
          token,
          units,
          user,
          roles,
          updateUnits: this.updateUnits,
          isLoggingIn: this.state.isLoggingIn,
          visibleOnboarding: this.state.visibleOnboarding,
          onChangeVisibleOnboarding: this.onChangeVisibleOnboarding,
          userPermissions,
          defaultPermissions: this.state.defaultPermissions,
          updateNotification: this.updateNotification,
          updateNotificationVar: this.state.updateNotificationVar,
          scheduled: this.state.scheduled,
          loadSchedules: this.loadScheduled
        }}
      >
        <Router>
          <PageWrapper>
            <Fragment>
              {!!authorized && (<PageHeader />)}
              <PageContent>
                {!!authorized && (<PageSideNav />)}
                <Switch>
                  <PrivateRoute exact path={Routes.home} component={roles && roles.length ? HomePage : HomeNoPermission} />
                  <PrivateRoute exact path={Routes.login} component={WrappedLoginContainer} />
                  <Route exact path={Routes.recover} component={RecoverPage} />
                  <Route excat path={`${Routes.code}`} component={CodePage} />
                  {roles && roles.length &&
                    <React.Fragment>
                      <PrivateRoute exact path={`${Routes.agents}/:id`} component={AgentsPage} />
                      <PrivateRoute exact path={`${Routes.auditeds}/:id`} component={AuditedDetailsPage} />
                      <PrivateRoute exact path={`${Routes.auditors}/:id`} component={AuditorDetailsPage} />
                      <PrivateRoute exact path={`${Routes.componentFormflow}/:id`} component={FormflowComponentSections} />
                      <PrivateRoute exact path={`${Routes.executionprocess}/:ticketId/process/:eventId`} component={ExecutionProcess} />
                      <PrivateRoute exact path={`${Routes.executionprocess}/newProcessExecution`} component={ExecutionProcess} />
                      <PrivateRoute exact path={`${Routes.ticketDetails}/:id`} component={TicketDetailsPage} />
                      <PrivateRoute exact path={`${Routes.ticketDetailsEhss}/:id`} component={TicketDetailsEHSS} />
                      <PrivateRoute exact path={`${Routes.workflow}/:id`} component={WorkflowContainer} />
                      <PrivateRoute exact path={Routes.adminHistory} component={HistoryAdminPage} />
                      <PrivateRoute exact path={Routes.agentsAdmin} component={AgentsAdminPage} />
                      <PrivateRoute exact path={Routes.alerts} component={AlertsPage} />
                      <PrivateRoute exact path={Routes.assignment} component={AssignmentPage} />
                      <PrivateRoute exact path={Routes.auditeds} component={AuditedsPage} />
                      <PrivateRoute exact path={Routes.auditors} component={AuditorsContainer} />
                      <PrivateRoute exact path={Routes.componentFormflow} component={FormflowComponentSections} />
                      <PrivateRoute exact path={Routes.dashboard} component={DashboardPage} />
                      <PrivateRoute exact path={Routes.distributors} component={DistributorsPage} />
                      <PrivateRoute exact path={Routes.ehssHistory} component={HistoryEHSSContainerComponent} />
                      <PrivateRoute exact path={Routes.qaHistory} component={HistoryQAContainerComponent} />
                      <PrivateRoute exact path={Routes.export} component={ExportContainer} />
                      <PrivateRoute exact path={Routes.formflowProcess} component={FormflowContainer} />
                      <PrivateRoute exact path={Routes.goals} component={AuditSettings} />
                      <PrivateRoute exact path={Routes.history} component={HistoryPage} />
                      <PrivateRoute exact path={Routes.links} component={LinksPage} />
                      <PrivateRoute exact path={Routes.manager} component={ManagerContainer} />
                      <PrivateRoute exact path={Routes.model3d} component={UploadModel3DContainer} />
                      <PrivateRoute exact path={Routes.notifications} component={NotificationsContainer} />
                      <PrivateRoute exact path={Routes.permissions} component={PermissionsPage} />
                      <PrivateRoute exact path={Routes.productionLines} component={ProductionLineComponent} />
                      <PrivateRoute exact path={Routes.products} component={ProductsContainerComponent} />
                      <PrivateRoute exact path={Routes.profile} component={ProfilePage} />
                      <PrivateRoute exact path={Routes.client} component={ClientContainer} />
                      <PrivateRoute exact path={Routes.units} component={UnitsPage} />
                      <PrivateRoute exact path={Routes.users} component={UsersContainer} />
                      <PrivateRoute exact path={Routes.targets} component={TargetsContainer} />
                      <PrivateRoute exact path={Routes.workflows} component={WorkflowsContainer} />
                      <PrivateRoute exact path={Routes.workstations} component={WorkstationProductionLineComponent} />
                      <PrivateRoute exact path={Routes.processManagement} component={ProcessManagement} />
                      <PrivateRoute exact path={Routes.liteProcesses} component={LiteProcesses} />
                      <PrivateRoute exact path={Routes.process} component={ProcessContainerPage} />
                      <PrivateRoute exact path={`${Routes.process}/:id`} component={ProcessContainerPage} />
                      {/* <PrivateRoute exact path={Routes.liteReports} component={liteDashboard} /> */}
                      <PrivateRoute exact path={Routes.liteReports} component={pillar === "LI" ? DashboardList : liteDashboard} />
                      <PrivateRoute exact path={`${Routes.liteReports}/:id`} component={liteDashboard} />
                      <PrivateRoute exact path={Routes.scheduler} component={SchedulerContainer} />
                      <PrivateRoute exact path={Routes.my_processes} component={MyProcessesContainer} />
                    </React.Fragment>
                  }

                </Switch>
              </PageContent>
            </Fragment>
          </PageWrapper>
        </Router>
      </AppContext.Provider >
    );
  }
}

injectGlobal`
  @import url('https://fonts.googleapis.com/css?family=Poppins&display=swap');
`